import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save } from "lucide-react";

const BLANK = {
  slug: "",
  name: "",
  type: "sock",
  price: 18.0,
  currency: "gbp",
  description: "",
  image: "",
  features: [],
  care: [],
  sizes: ["UK 3-6", "UK 6-9", "UK 9-12"],
  default_grip: "grid",
  season: "FW26",
  is_active: true,
  is_featured: false,
  sort_order: 0,
};

/** ProductsManager — CRUD for products. List + create + delete + inline-edit. */
const ProductsManager = ({ authClient, onProductsChange }) => {
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState(BLANK);
  const [savingId, setSavingId] = useState(null);

  const reload = async () => {
    try {
      const { data } = await authClient().get("/admin/cms/products");
      setItems(data);
      onProductsChange?.(data);
    } catch {
      toast.error("Couldn't load products");
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onFieldChange = (idx, key, val) => {
    setItems((prev) =>
      prev.map((p, i) => (i === idx ? { ...p, [key]: val, _dirty: true } : p))
    );
  };

  const onSave = async (p) => {
    setSavingId(p.id);
    try {
      await authClient().put(`/admin/cms/products/${p.id}`, {
        slug: p.slug,
        name: p.name,
        type: p.type || "sock",
        price: Number(p.price),
        currency: p.currency || "gbp",
        description: p.description || "",
        image: p.image || "",
        features: p.features || [],
        care: p.care || [],
        sizes: p.sizes || [],
        default_grip: p.default_grip || null,
        season: p.season || "FW26",
        is_active: Boolean(p.is_active),
        is_featured: Boolean(p.is_featured),
        sort_order: Number(p.sort_order) || 0,
      });
      toast.success(`${p.name} saved`);
      reload();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed");
    } finally {
      setSavingId(null);
    }
  };

  const onDelete = async (p) => {
    if (
      !window.confirm(
        `Delete "${p.name}" and ALL its colourways? This cannot be undone.`
      )
    )
      return;
    try {
      await authClient().delete(`/admin/cms/products/${p.id}`);
      toast.success("Product removed");
      reload();
    } catch {
      toast.error("Delete failed");
    }
  };

  const onCreate = async () => {
    if (!draft.slug || !draft.name) {
      toast.error("Slug and name are required");
      return;
    }
    try {
      await authClient().post("/admin/cms/products", {
        ...draft,
        price: Number(draft.price),
        sort_order: Number(draft.sort_order) || 0,
      });
      toast.success(`${draft.name} created`);
      setCreating(false);
      setDraft(BLANK);
      reload();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Create failed");
    }
  };

  return (
    <div data-testid="products-manager">
      <div className="flex items-center justify-between mb-4">
        <p className="overline">Products ({items.length})</p>
        <button
          type="button"
          onClick={() => setCreating((v) => !v)}
          className="btn-secondary !px-4 !py-2 text-xs"
          data-testid="product-add-button"
        >
          <Plus size={14} className="mr-1.5" /> {creating ? "Cancel" : "New product"}
        </button>
      </div>

      {creating && (
        <div
          className="bg-white border border-ink p-4 mb-4 grid grid-cols-2 md:grid-cols-4 gap-3 items-end"
          data-testid="product-create-row"
        >
          {[
            ["slug", "Slug"],
            ["name", "Name"],
            ["type", "Type (sock / generic)"],
            ["price", "Price (£)"],
            ["season", "Season"],
            ["image", "Hero image URL"],
          ].map(([k, label]) => (
            <label key={k} className="block">
              <span className="text-[11px] tracking-[0.16em] uppercase font-bold block mb-1">
                {label}
              </span>
              <input
                value={draft[k]}
                onChange={(e) => setDraft({ ...draft, [k]: e.target.value })}
                className="w-full border border-line px-3 py-2 bg-chalk"
                type={k === "price" ? "number" : "text"}
                step={k === "price" ? "0.01" : undefined}
              />
            </label>
          ))}
          <label className="block col-span-2 md:col-span-4">
            <span className="text-[11px] tracking-[0.16em] uppercase font-bold block mb-1">
              Description
            </span>
            <textarea
              rows={2}
              value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              className="w-full border border-line px-3 py-2 bg-chalk"
            />
          </label>
          <button
            type="button"
            onClick={onCreate}
            className="btn-primary"
            data-testid="product-create-submit"
          >
            Create product
          </button>
        </div>
      )}

      <div className="bg-white border border-line overflow-x-auto">
        <table className="w-full text-xs min-w-[900px]">
          <thead className="border-b border-line">
            <tr className="text-left">
              <th className="px-3 py-3 overline">Name / Slug</th>
              <th className="px-3 py-3 overline">Type</th>
              <th className="px-3 py-3 overline">Price (£)</th>
              <th className="px-3 py-3 overline">Season</th>
              <th className="px-3 py-3 overline">Featured</th>
              <th className="px-3 py-3 overline">Active</th>
              <th className="px-3 py-3 overline">Sort</th>
              <th className="px-3 py-3" />
            </tr>
          </thead>
          <tbody>
            {items.map((p, idx) => (
              <tr key={p.id} className="border-b border-line" data-testid={`product-row-${p.slug}`}>
                <td className="px-3 py-2">
                  <input
                    value={p.name}
                    onChange={(e) => onFieldChange(idx, "name", e.target.value)}
                    className="w-full border border-transparent hover:border-line focus:border-ink bg-transparent px-1.5 py-1"
                  />
                  <input
                    value={p.slug}
                    onChange={(e) => onFieldChange(idx, "slug", e.target.value)}
                    className="w-full mono text-[10px] text-mute border border-transparent hover:border-line bg-transparent px-1.5"
                  />
                </td>
                <td className="px-3 py-2">
                  <select
                    value={p.type || "sock"}
                    onChange={(e) => onFieldChange(idx, "type", e.target.value)}
                    className="border border-line bg-chalk px-1.5 py-1"
                  >
                    <option value="sock">sock</option>
                    <option value="generic">generic</option>
                  </select>
                </td>
                <td className="px-3 py-2">
                  <input
                    type="number"
                    step="0.01"
                    value={p.price}
                    onChange={(e) => onFieldChange(idx, "price", e.target.value)}
                    className="w-20 border border-line bg-chalk px-1.5 py-1 mono"
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    value={p.season || ""}
                    onChange={(e) => onFieldChange(idx, "season", e.target.value)}
                    className="w-16 border border-line bg-chalk px-1.5 py-1 mono"
                  />
                </td>
                <td className="px-3 py-2 text-center">
                  <input
                    type="checkbox"
                    checked={Boolean(p.is_featured)}
                    onChange={(e) => onFieldChange(idx, "is_featured", e.target.checked)}
                  />
                </td>
                <td className="px-3 py-2 text-center">
                  <input
                    type="checkbox"
                    checked={Boolean(p.is_active)}
                    onChange={(e) => onFieldChange(idx, "is_active", e.target.checked)}
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="number"
                    value={p.sort_order ?? 0}
                    onChange={(e) => onFieldChange(idx, "sort_order", e.target.value)}
                    className="w-12 border border-line bg-chalk px-1.5 py-1 mono"
                  />
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {p._dirty && (
                    <button
                      type="button"
                      onClick={() => onSave(p)}
                      disabled={savingId === p.id}
                      className="text-[10px] tracking-widest uppercase font-bold px-2 py-1 bg-ink text-white mr-2"
                      data-testid={`product-save-${p.slug}`}
                    >
                      <Save size={11} className="inline mr-1" />
                      {savingId === p.id ? "…" : "Save"}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => onDelete(p)}
                    className="text-mute hover:text-ink p-1"
                  >
                    <Trash2 size={12} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ProductsManager;
